"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { useTranslations } from "use-intl";

import {
  ChevronRightIcon,
  LogoutIcon,
  PersonIcon,
  SettingsIcon,
  getListIcon as resolveListIcon,
} from "@/components/appIcons";
import { Link } from "@/components/link";
import { LoginLink } from "@/components/login-link";
import { MobileMenu } from "@/components/mobile-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLists } from "@/contexts/lists-context";
import { useHydrated } from "@/hooks/use-hydrated";
import { useIsMobile } from "@/hooks/use-media-query";
import { useSession } from "@/hooks/use-session";
import { authClient } from "@/lib/auth-client";
import { detachCurrentBrowserPush } from "@/lib/detachCurrentBrowserPush";
import { getCurrentBrowserRedirectPath, setRedirectAfterLogin } from "@/lib/redirect-after-login";
import { cn } from "@/lib/utils";
import { clearSession } from "@/utils/session";
import { isSyntheticEmail } from "@garimpa/lib/isSyntheticEmail";

import { Button } from "./ui/button";
import { Skeleton } from "./ui/skeleton";

function defaultListIcon(type: string, icon: string | null): string {
  if (icon) return icon;
  if (type === "collection") return "library_music";
  if (type === "wishlist") return "favorite";
  return "queue_music";
}

function getListHref(list: { id: string }): string {
  return `/lists/${list.id}`;
}

function getListIconColor(type: string): string {
  if (type === "collection") return "text-primary";
  if (type === "wishlist") return "text-red-400";
  return "text-muted-foreground";
}

function AvatarButton({
  ariaLabel,
  initial,
  image,
  onClick,
}: {
  ariaLabel: string;
  initial: string;
  image?: string | null;
  onClick?: () => void;
}) {
  return (
    <button
      aria-label={ariaLabel}
      onClick={onClick}
      className="relative after:absolute after:-inset-1.5 after:content-[''] flex items-center justify-center size-8 rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/80 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {image ? <img src={image} alt="" className="size-8 rounded-full object-cover" /> : initial}
    </button>
  );
}

function DesktopUserMenu({
  session,
  visibleLists,
  onSignOut,
}: {
  session: NonNullable<ReturnType<typeof useSession>["data"]>;
  visibleLists: ReturnType<typeof useLists>["allLists"];
  onSignOut: () => void;
}) {
  const router = useRouter();
  const t = useTranslations("globals.auth");
  const tProfile = useTranslations("globals.profile");
  const tLists = useTranslations("globals.lists");
  const tSettings = useTranslations("globals.settings");
  const initial = (session.user.name?.[0] ?? session.user.email[0]).toUpperCase();
  const isGuest = session.user.isAnonymous;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <button
            aria-label={tProfile("myProfile")}
            className="relative after:absolute after:-inset-1.5 after:content-[''] flex items-center justify-center size-8 rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/80 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        }
      >
        {session.user.image ? (
          <img src={session.user.image} alt="" className="size-8 rounded-full object-cover" />
        ) : (
          initial
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="bg-card min-w-64 p-1">
        <DropdownMenuGroup>
          <DropdownMenuLabel className="font-semibold text-sm">
            {session.user.name}
            {isGuest && (
              <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
                {t("guest")}
              </span>
            )}
          </DropdownMenuLabel>
          {!isSyntheticEmail(session.user.email) && (
            <DropdownMenuItem className="text-muted-foreground text-xs">
              {session.user.email}
            </DropdownMenuItem>
          )}
        </DropdownMenuGroup>

        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuLabel>
            <button
              onClick={() => router.navigate({ to: "/lists" })}
              className="flex items-center gap-1 hover:text-foreground transition-colors"
            >
              {tLists("title")}
              <ChevronRightIcon className="text-xs" />
            </button>
          </DropdownMenuLabel>
          {visibleLists.map((list) => {
            const Icon = resolveListIcon(defaultListIcon(list.type, list.icon));
            return (
              <DropdownMenuItem
                key={list.id}
                className="py-2"
                onClick={() => router.navigate({ to: getListHref(list) })}
              >
                <Icon className={cn("text-base mr-2", getListIconColor(list.type))} />
                <span className="flex-1 truncate">{list.name}</span>
                <span className="text-xs text-muted-foreground tabular-nums ml-2">
                  {list.itemCount}
                </span>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuGroup>

        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          {isGuest ? (
            <DropdownMenuItem
              className="py-2.5 text-primary"
              onClick={() =>
                router.navigate({
                  to: "/login",
                  search: { mode: "signup", redirect: router.state.location.href },
                })
              }
            >
              <PersonIcon className="text-base mr-2" />
              <span className="flex-1">{t("saveGuestActivity")}</span>
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              className="py-2.5"
              onClick={() =>
                router.navigate({
                  to: session.user.username
                    ? `/u/${session.user.username}`
                    : "/settings?setup=username",
                })
              }
            >
              <PersonIcon className="text-base mr-2" />
              <span className="flex-1">{tProfile("myProfile")}</span>
              {session.user.username && (
                <span className="text-xs text-muted-foreground ml-2">
                  /u/{session.user.username}
                </span>
              )}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem className="py-2.5" onClick={() => router.navigate({ to: "/settings" })}>
            <SettingsIcon className="text-base mr-2" />
            {tSettings("settings")}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem className="py-2.5" variant="destructive" onClick={onSignOut}>
            <LogoutIcon className="text-base mr-2" />
            {isGuest ? t("discardGuest") : t("signOut")}
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function UserMenu() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { data: session, isPending } = useSession();
  const t = useTranslations("globals.auth");
  const tProfile = useTranslations("globals.profile");
  const isMobile = useIsMobile();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { allLists } = useLists();
  const hydrated = useHydrated();
  const visibleLists = hydrated ? allLists : [];

  if (isPending) {
    return <Skeleton className="size-8 rounded-full" />;
  }

  if (!session) {
    return (
      <div className="flex items-center gap-2">
        <Link
          to="/login"
          rel="nofollow"
          className="hidden sm:block"
          onClick={() => {
            const redirectPath = getCurrentBrowserRedirectPath();
            if (redirectPath) setRedirectAfterLogin(redirectPath);
          }}
        >
          <Button variant="ghost" size="sm">
            {t("signIn")}
          </Button>
        </Link>
        <LoginLink mode="signup">
          <Button size="sm">{t("createAccount")}</Button>
        </LoginLink>
      </div>
    );
  }

  const initial = (session.user.name?.[0] ?? session.user.email[0]).toUpperCase();
  const isGuest = session.user.isAnonymous;
  const discardOrSignOut = async () => {
    if (isGuest && !window.confirm(t("discardGuestConfirm"))) return;

    try {
      await detachCurrentBrowserPush();
    } catch {
      toast.error(t("signOutError"));
      return;
    }

    if (isGuest) {
      const result = await authClient.deleteAnonymousUser();
      if (result.error) {
        toast.error(result.error.message ?? t("discardGuestError"));
        return;
      }
    } else {
      const result = await authClient.signOut();
      if (result.error) {
        toast.error(result.error.message ?? t("signOutError"));
        return;
      }
    }

    clearSession(queryClient);
    await router.navigate({ to: "/" });
  };

  if (isMobile) {
    return (
      <>
        <AvatarButton
          ariaLabel={tProfile("myProfile")}
          initial={initial}
          image={session.user.image}
          onClick={() => setMobileOpen(true)}
        />
        <MobileMenu session={session} open={mobileOpen} onClose={() => setMobileOpen(false)} />
      </>
    );
  }

  return (
    <DesktopUserMenu
      session={session}
      visibleLists={visibleLists}
      onSignOut={() => void discardOrSignOut()}
    />
  );
}
